import React, { useState, forwardRef } from 'react';
import { Star, Edit2, Archive, Trash2, Download, X, FolderOpen } from 'lucide-react';

const ConversationContextMenu = forwardRef(({
  conversation,
  position,
  projects,
  onStar,
  onRename,
  onArchive,
  onDelete,
  onChangeProject,
  onRemoveFromProject,
  onClose
}, ref) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(conversation.title);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showProjectSelect, setShowProjectSelect] = useState(false);

  const handleRename = () => {
    if (newTitle.trim() && newTitle !== conversation.title) {
      onRename(newTitle);
    }
    setIsRenaming(false);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  // Smart positioning: flip menu upward if it would go off-screen at bottom
  const getMenuStyle = () => {
    const menuHeight = isRenaming || showDeleteConfirm ? 200 : showProjectSelect ? 400 : 300;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - position.y;
    
    // If not enough space below, position menu above the click point
    const shouldFlipUp = spaceBelow < menuHeight;
    
    return {
      position: 'fixed',
      top: shouldFlipUp ? 'auto' : `${position.y}px`,
      bottom: shouldFlipUp ? `${viewportHeight - position.y}px` : 'auto',
      left: `${position.x}px`,
      zIndex: 1000
    };
  };

  const menuStyle = getMenuStyle();

  // RENAME MODAL
  if (isRenaming) {
    return (
      <div
        ref={ref}
        className="vault-menu"
        style={{ ...menuStyle, minWidth: 300, padding: 0, overflow: 'hidden' }}
      >
        {/* Header */}
        <div
          className="vault-label"
          style={{ justifyContent: 'space-between', padding: '14px 16px', marginBottom: 0, borderBottom: '1px solid var(--rule)' }}
        >
          <span>Rename Conversation</span>
          <button className="vault-icon-btn" style={{ width: 28, height: 28 }} onClick={onClose}>
            <X size={15} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleRename()}
            autoFocus
            className="vault-field"
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="vault-btn vault-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="vault-btn vault-btn--primary" onClick={handleRename}>
              Rename
            </button>
          </div>
        </div>
      </div>
    );
  }

  // DELETE CONFIRMATION MODAL
  if (showDeleteConfirm) {
    return (
      <div
        ref={ref}
        className="vault-menu"
        style={{ ...menuStyle, minWidth: 300, padding: 0, overflow: 'hidden' }}
      >
        {/* Header */}
        <div
          className="vault-label"
          style={{ justifyContent: 'space-between', padding: '14px 16px', marginBottom: 0, borderBottom: '1px solid var(--rule)' }}
        >
          <span>Delete Conversation?</span>
          <button className="vault-icon-btn" style={{ width: 28, height: 28 }} onClick={onClose}>
            <X size={15} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ color: 'var(--fg-dim)', fontSize: 13, margin: 0 }}>
            This will permanently delete "{conversation.title}"
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="vault-btn vault-btn--ghost" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </button>
            <button className="vault-btn vault-btn--danger" onClick={handleDelete}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PROJECT SELECT MODAL
  if (showProjectSelect) {
    return (
      <div
        ref={ref}
        className="vault-menu"
        style={{ ...menuStyle, minWidth: 250, maxWidth: 300, padding: 0, overflow: 'hidden' }}
      >
        {/* Header */}
        <div
          className="vault-label"
          style={{ justifyContent: 'space-between', padding: '14px 16px', marginBottom: 0, borderBottom: '1px solid var(--rule)' }}
        >
          <span>Change Project</span>
          <button className="vault-icon-btn" style={{ width: 28, height: 28 }} onClick={onClose}>
            <X size={15} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 6, maxHeight: 300, overflowY: 'auto' }}>
          {projects && projects.length > 0 ? (
            <>
              {projects.map(project => (
                <button
                  key={project.id}
                  className="vault-menu-item"
                  style={
                    conversation.projectId === project.id
                      ? { background: 'var(--accent-tint)', color: 'var(--fg)' }
                      : undefined
                  }
                  onClick={() => {
                    onChangeProject(project.id);
                    onClose();
                  }}
                >
                  <FolderOpen size={15} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</span>
                </button>
              ))}

              {conversation.projectId && (
                <>
                  <div className="vault-menu-sep" />
                  <button
                    className="vault-menu-item is-danger"
                    onClick={() => {
                      onRemoveFromProject();
                      onClose();
                    }}
                  >
                    <X size={15} />
                    <span>Remove from project</span>
                  </button>
                </>
              )}
            </>
          ) : (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <p style={{ color: 'var(--fg-dim)', fontSize: 13, margin: 0 }}>No projects available</p>
              <p style={{ color: 'var(--fg-faint)', fontSize: 11, marginTop: 4 }}>Create a project first</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // MAIN CONTEXT MENU
  return (
    <div
      ref={ref}
      className="vault-menu"
      style={menuStyle}
    >
      <button
        className="vault-menu-item"
        onClick={() => {
          onStar();
          onClose();
        }}
      >
        <Star
          size={15}
          style={conversation.starred ? { color: 'oklch(0.80 0.14 70)', fill: 'oklch(0.80 0.14 70)' } : undefined}
        />
        <span>{conversation.starred ? 'Unstar' : 'Star'} Conversation</span>
      </button>

      <button
        className="vault-menu-item"
        onClick={() => setIsRenaming(true)}
      >
        <Edit2 size={15} />
        <span>Rename</span>
      </button>

      <button
        className="vault-menu-item"
        onClick={() => setShowProjectSelect(true)}
      >
        <FolderOpen size={15} />
        <span>Change project</span>
      </button>

      <button
        className="vault-menu-item"
        onClick={() => {
          onArchive();
          onClose();
        }}
      >
        <Archive size={15} />
        <span>{conversation.archived ? 'Unarchive' : 'Archive'}</span>
      </button>

      <div className="vault-menu-sep" />

      <button
        className="vault-menu-item is-danger"
        onClick={() => setShowDeleteConfirm(true)}
      >
        <Trash2 size={15} />
        <span>Delete</span>
      </button>
    </div>
  );
});

ConversationContextMenu.displayName = 'ConversationContextMenu';

export default ConversationContextMenu;